import { useState, useCallback } from 'react';
import { useSessionStorage } from 'react-use';
import useExpandCollapseAllState from 'modules/common/hooks/useExpandCollapseAllState';
import { useMeContext } from 'modules/me';

const getTransformedAccordianMapForState = (
  map: Record<string, boolean>,
  state: boolean
): Record<string, boolean> =>
  Object.keys(map).reduce(
    (acc, value) => ({
      ...acc,
      [value]: Boolean(state)
    }),
    {} as Record<string, boolean>
  );

export const useGroupedNotesExpansion = (): {
  groupedNotesExpansionState: Record<string, boolean>;
  expandAll: boolean;
  onExpandCollapse: (date: string) => void;
  onExpandCollapseAll: (state: boolean) => void;
} => {
  const { id } = useMeContext() ?? {};
  const expandCollapseAllId = `${id}_mywork_expandAll`;
  const notesListItemAccordianStateKey = `${id}_NotesListItemAccordianState`;
  const { expandAllState } = useExpandCollapseAllState(expandCollapseAllId);

  const [
    notesListItemAccordianStateMap,
    setNotesListItemAccordianStateMap
  ] = useSessionStorage(notesListItemAccordianStateKey, {});

  const [expandAll, setExpandAll] = useState(expandAllState);

  const [noteExpansionMap, setNoteExpansionMap] = useState<
    Record<string, boolean>
  >(notesListItemAccordianStateMap);

  const onExpandCollapse = useCallback(
    (date: string) => {
      setNoteExpansionMap((previousExpansionMap) => {
        const newNoteExpansionMap = {
          ...previousExpansionMap,
          [date]:
            previousExpansionMap[date] === undefined
              ? !expandAll
              : !previousExpansionMap[date]
        };

        setNotesListItemAccordianStateMap(newNoteExpansionMap);

        return newNoteExpansionMap;
      });
    },
    [expandAll, setNotesListItemAccordianStateMap]
  );

  const onExpandCollapseAll = useCallback(
    (state: boolean) => {
      setExpandAll(state);
      setNoteExpansionMap((previousMap) => {
        const newNoteExpansionMap = getTransformedAccordianMapForState(
          previousMap,
          state
        );

        setNotesListItemAccordianStateMap(newNoteExpansionMap);

        return newNoteExpansionMap;
      });
    },
    [setNoteExpansionMap, setNotesListItemAccordianStateMap]
  );

  return {
    groupedNotesExpansionState: noteExpansionMap,
    expandAll,
    onExpandCollapse,
    onExpandCollapseAll
  };
};
